import { throttle as _throttle } from 'underscore';
import { Inject, Injectable } from '@angular/core';
import { HubConnectionState } from '@microsoft/signalr';
import { Subscription } from 'rxjs';

import { SignalRHubsConnection } from 'rev-shared/push/SignalRHubsConnection';
import { SignalRHubsConnectionToken } from 'rev-shared/push/SignalRHubsConnectionToken';

import { UserAuthenticationService } from './UserAuthentication.Service';
import { UserContextService } from './UserContext.Service';

export interface ISessionKeepalive {
	begin(): ISessionKeepalive;
	beginWhenConnected(): ISessionKeepalive;
	end(): ISessionKeepalive;
}

@Injectable({
	providedIn: 'root'
})
export class SessionService {
	private readonly sessionKeepAliveInterval: number = 5 * 60000;

	constructor(
		@Inject(SignalRHubsConnectionToken) private readonly SignalRHubsConnection: SignalRHubsConnection,
		private readonly UserAuthenticationService: UserAuthenticationService,
		private readonly UserContext: UserContextService
	) { }

	public createKeepalive(): ISessionKeepalive {
		const sessionSvc: SessionService = this;
		let timer: number;
		let sub: Subscription;

		const keepaliveReturn: ISessionKeepalive = {
			begin(): ISessionKeepalive {
				if (!sessionSvc.UserContext.isUserLoggedIn()) {
					return;
				}

				keepAlive();

				function keepAlive() {
					timer = window.setTimeout(keepAlive, sessionSvc.sessionKeepAliveInterval);
					sessionSvc.extendSessionTimeoutImpl();
				}

				return this;
			},

			beginWhenConnected(): ISessionKeepalive {
				// if connected to SignalR, then go ahead and begin the keepAlive
				if (sessionSvc.SignalRHubsConnection.getConnectionStatus() === HubConnectionState.Connected) {
					this.begin();
				} else { // otherwise, wait for the connection and then begin
					sub = sessionSvc.SignalRHubsConnection.stateChanged$.subscribe(newState => {
						if (newState === HubConnectionState.Connected) {
							sub.unsubscribe();
							this.begin();
						}
					});
				}

				return this;
			},

			end(): ISessionKeepalive {
				window.clearTimeout(timer);
				timer = null;
				sub?.unsubscribe();

				return this;
			}
		};

		return keepaliveReturn;
	}


	public tryExtendTimeout(): void {
		if (!this.UserContext.isUserLoggedIn() || this.UserContext.isRegisteredGuest()) {
			return;
		}
		this.extendSessionTimeoutImpl();
	}

	private readonly extendSessionTimeoutImpl = _throttle(() => {
		if (this.UserContext.isUserAuthenticated() && this.SignalRHubsConnection.isConnected()) {
			this.UserAuthenticationService.extendSessionTimeout(this.UserContext.getUser().id)
				.catch((err: any) => {
					console.log('Session Ended', err);
				});
		}
	}, this.sessionKeepAliveInterval);

	public start(): void {
		// Attempt to extend session on reconnect. This forces the UI to bounce back to the login page if the session ended while the connection was down.
		this.SignalRHubsConnection.reconnected$.subscribe(() => this.tryExtendTimeout());
	}
}
